/**
 * Minimal mirror of the TurboTwin platform Bevy-viewer bridge protocol.
 *
 * A hand-copied subset of the platform's public embed contract (turbotwin repo
 * `webclient/src/components/Viewer3D/protocol.ts` + `bevy-client/src/plugins/
 * bridge/protocol.rs`). We vendor only the message shapes the Orientation
 * Editor uses; the platform owns the full protocol and its fixture tripwire.
 * Keep this in sync with the viewer bundle version served under `/bevy`
 * (dev: `serveBevyPlugin` in `vite.config.ts`; prod: the copied `bevy-dist`).
 * NOTE (2026-08-28): followTwin's distanceMilli / pitchMilli need a viewer
 * stamp built from TurboTwin >= 22cccbc; an older /bevy bundle drops them.
 *
 * WIRE RULE (load-bearing): every inbound numeric field must be an integer or a
 * string — never a fractional JSON float. The viewer's deserializer drops the
 * whole message silently on a fractional float, with no error back to the host.
 * Outbound messages may carry f64.
 *
 * Geo-camera messages (`flyToGeo` / `getCameraPose` → `cameraPose`, platform
 * "E7") ARE included below: E7 shipped in the platform and is present in the
 * deployed `/bevy` dist (verified against `portal.minetwin.ai/bevy/` 2026-08-19).
 * They encode lat/lon as integer microdegrees (`latE7`/`lonE7`) and offsets in
 * milli-units for exactly the wire-rule reason above; the outbound `cameraPose`
 * reply may carry plain f64.
 *
 * Measure tool (platform protocol level 33): `measure` in, `measureChanged`
 * out, mirrored by hand from the platform SDK's `viewer-protocol.ts`
 * (`MeasureMessage`, `MeasureChangedMessage`). The editor feature-detects it
 * from `ready.protocolVersion` and hides the button below 33. Level 35 adds
 * `measure.mode` (`points` | `size`) and `measureChanged.size` (`MeasureSize`):
 * the Size mode pins a clicked twin's oriented bounding box.
 *
 * `showTwinInfo` / `twinInfo` are NOT in the platform SDK's TypeScript
 * protocol: the verb exists only in the viewer's Rust bridge. Kept here on
 * purpose, since the editor is its only host.
 */

import type { CameraParamsWire } from './cameraModes';

/** Host → viewer. */
export type InboundMessage =
  | { type: 'auth'; token: string; orgId: string; projectId: string }
  | { type: 'mode'; mode: 'project' }
  | { type: 'select'; twinId: string }
  // Absolute selection SET (level 13) — outline these twins; [] clears. Unlike
  // `select` it never moves the camera. The editor previews SelectTwins with the
  // set and ClearSelection with []. The viewer already handles this message.
  | { type: 'setSelection'; twinIds: string[] }
  // Fade a SET of twins to an opacity — the SetTransparency beat's 3D preview.
  // opacityMilli = opacity × 1000 (INTEGER per the wire rule; 250 = 25% opaque),
  // and each message REPLACES the faded set: [] restores all, and opacityMilli
  // >= 1000 restores a listed twin (mirrors "opacity >= 1 restores"). Unlike
  // setSelection this is a material fade, not an outline. Additive (level 14) —
  // silently dropped by a /bevy dist built before the viewer verb, so the
  // preview is a no-op until the bundle is republished.
  | { type: 'setTwinTransparency'; twinIds: string[]; opacityMilli: number; dither?: boolean }
  | { type: 'focus'; twinId: string }
  | { type: 'frameScene' }
  // E7 — fly the orbit camera to a geodetic point. INTEGER-ONLY per the wire
  // rule: latE7/lonE7 = degrees × 1e7, elevMilli/rangeMilli = metres × 1000,
  // heading/pitchMilli = compass degrees × 1000 (heading 0=N, clockwise;
  // pitch < 0 = looking down). Absent heading/pitch/range keeps the camera's
  // current value; absent durationMillis = the viewer's ~190 ms default.
  | {
      type: 'flyToGeo';
      latE7: number;
      lonE7: number;
      elevMilli: number;
      headingMilli?: number;
      pitchMilli?: number;
      rangeMilli?: number;
      durationMillis?: number;
    }
  // E7 — fly the camera along an ordered list of geodetic waypoints (the
  // multi-location `FlyToLocation` / "fly along path" preview). Same integer-only
  // wire rule as flyToGeo: each point is latE7/lonE7 = deg×1e7, elevMilli = m×1000.
  // Absent durationMillis = the viewer derives it from the path length. Added
  // additively (platform verb, bevy 8bd0190c73+); a viewer built before it
  // silently drops it, so the preview is a no-op until the /bevy bundle carries it.
  | {
      type: 'flyAlongPath';
      points: { latE7: number; lonE7: number; elevMilli: number }[];
      durationMillis?: number;
    }
  // Set the GLOBAL playback speed of all camera motion — the orientation
  // editor's play-speed control. The viewer scales its virtual clock, so every
  // timed camera verb (fly-to, flyAlongPath, orbit, look, focus) AND the free
  // camera run proportionally faster or slower. factorMilli = multiplier × 1000
  // (INTEGER per the wire rule; 1000 = normal, 2000 = 2×, 500 = half); the
  // viewer clamps it. Global + persistent until the next setPlaybackSpeed. Added
  // additively (bevy protocol 24); a viewer built before it silently drops it,
  // so the Play-speed dropdown is a no-op until the /bevy bundle carries it. The
  // host must scale its OWN beat / narration timing by the same factor — this
  // message only paces what the viewer renders.
  | { type: 'setPlaybackSpeed'; factorMilli: number }
  // Draw (or replace) a host overlay by id — the viewer's persistent line /
  // polygon / point gizmo (platform level 12). Used to render an authored fly
  // path as a line in 3D. INTEGER E7/milli coords like flyToGeo; `kind` is
  // lowercase. Re-sending the same id replaces it; `removeOverlay` clears it.
  // Silently dropped by a pre-level-12 /bevy dist.
  | {
      type: 'setOverlay';
      id: string;
      kind: 'point' | 'line' | 'polygon';
      coords: { latE7: number; lonE7: number; elevMilli?: number }[];
      style?: { color?: string; widthPx?: number; fillOpacityMilli?: number; pointSizePx?: number;
        /** POINT marker shape: 'dot' (default), 'pulse' (pulsing location marker)
         *  or 'arrow' (down-pointing, bobbing). No-op on a pre-level-12 /bevy. */
        marker?: 'dot' | 'pulse' | 'arrow' };
      label?: string;
    }
  | { type: 'removeOverlay'; id: string }
  // E7 — ask where the camera is; the viewer replies with exactly one
  // `cameraPose` echoing this requestId (a string on purpose, so it dodges the
  // integer-only rule).
  | { type: 'getCameraPose'; requestId: string }
  // Cinematic verbs (platform protocol v7) — the twin-targeted counterparts of
  // `flyToGeo`, used to PREVIEW an authored tour camera command in the embedded
  // viewer. Same integer-only wire rule: twin ids are strings (exempt);
  // range/heading/pitchMilli and durationMillis are integers in the same units
  // as flyToGeo (rangeMilli = m×1000, heading/pitchMilli = deg×1000, absent =
  // the viewer's default). A twin id resolves to that twin's world bounds inside
  // the viewer, so no geo origin is needed. Silently dropped by a pre-v7 /bevy
  // dist — the preview button is a no-op until the viewer bundle is republished.
  | {
      type: 'flyToTwin';
      twinId: string;
      rangeMilli?: number;
      headingMilli?: number;
      pitchMilli?: number;
      durationMillis?: number;
    }
  // Rotate in place to face a twin, WITHOUT moving the camera (focus→target,
  // radius = current distance). Absent durationMillis = the viewer's default.
  | { type: 'lookAtTwin'; twinId: string; durationMillis?: number }
  // Same as lookAtTwin but toward a geodetic point (integer E7/milli, like
  // flyToGeo). Needs the project origin, which the viewer resolves lazily.
  | {
      type: 'lookAtGeo';
      latE7: number;
      lonE7: number;
      elevMilli: number;
      durationMillis?: number;
    }
  // Fly to one twin while aiming the camera at another (the `FlyAndLookAt` tour
  // command). Both targets are twin ids; range/duration are optional integers.
  | {
      type: 'flyToTwinLookAtTwin';
      flyTwinId: string;
      lookAtTwinId: string;
      rangeMilli?: number;
      durationMillis?: number;
    }
  // Generalized Fly + look: fly to one target while aiming at another for the
  // whole flight, where EACH of fly/look is either a twin (`*TwinId`) or a geo
  // coordinate (`*LatE7`/`*LonE7`/`*ElevMilli`, integer E7/milli like flyToGeo).
  // Superset of flyToTwinLookAtTwin; provide exactly one of twin-id or the
  // lat/lon/elev triple per target. Added additively (no protocol-version bump):
  // a viewer built before it silently drops it, so a geo-target preview is a
  // no-op until the /bevy bundle is republished.
  | {
      type: 'flyToLookAt';
      flyTwinId?: string;
      flyLatE7?: number;
      flyLonE7?: number;
      flyElevMilli?: number;
      lookTwinId?: string;
      lookLatE7?: number;
      lookLonE7?: number;
      lookElevMilli?: number;
      rangeMilli?: number;
      durationMillis?: number;
    }
  // Continuous camera motions — the CONTINUOUS_CMDS (Orbit / Follow) previews.
  // Unlike the one-shot verbs these run every frame until `stopCamera`, so the
  // preview button toggles to "Stop Preview" while one is active. Integer-only
  // wire rule: twin id is a string (exempt); rotateSpeedMilli = deg/s × 1000,
  // distanceMilli = m × 1000 (orbit radius), pitchMilli = deg × 1000
  // (negative-down), all absent = the viewer's defaults. Added additively (no
  // protocol-version bump); silently dropped by a viewer built before them, so
  // the toggle is a no-op until the /bevy bundle is republished.
  | {
      type: 'orbitTwin';
      twinId: string;
      rotateSpeedMilli?: number;
      distanceMilli?: number;
      pitchMilli?: number;
    }
  // Follow holds a fixed offset and tracks the twin as it moves. distanceMilli
  // (follow arm, m × 1000) and pitchMilli (deg × 1000, negative-down) are
  // optional — absent keeps the current arm / tilt. Unlike Orbit there is no
  // rotateSpeed and no duration: a follow runs until `stopCamera`, matching the
  // Unreal runtime (which drives Follow at rotateSpeed 0 / duration 0).
  | { type: 'followTwin'; twinId: string; distanceMilli?: number; pitchMilli?: number }
  | { type: 'stopCamera' }
  // Render translucent spike markers at a set of geodetic points — reused here
  // to show the orientation's stops in 3D. `data` is CSV, one
  // `latE7,lonE7,elevMilli` per line (same integer units as flyToGeo); an empty
  // string clears, and each message REPLACES the whole set. `ordered:true` (the
  // orientation-tour case) additionally draws a follow-line with direction
  // arrows through the stops in CSV order and billboards each stop's 1-based
  // index; omitted/false is the flat unordered import-preview look.
  | { type: 'ghostMarkers'; data: string; ordered?: boolean }
  // Highlight ONE geodetic point — a pulsing marker at the currently selected
  // geo location (the 3D twin of the 2D map's command marker). Integer E7/milli
  // like flyToGeo; ALL coords present = show, any absent = clear. Additive —
  // a pre-existing /bevy dist drops it silently (no marker, nothing breaks).
  | { type: 'highlightGeo'; latE7?: number; lonE7?: number; elevMilli?: number }
  // Second, GREEN geo marker shown alongside highlightGeo (orange) — the Fly +
  // look FLY location. Same coords/clear semantics; additive (older /bevy drops).
  | { type: 'highlightFly'; latE7?: number; lonE7?: number; elevMilli?: number }
  // Highlight a twin by id — the viewer resolves its ACTUAL position (mesh
  // centre) and drives the same marker, so the cue lands on the twin rather than
  // its stored geo anchor. Additive; an older /bevy dist ignores it.
  | { type: 'highlightTwin'; twinId: string }
  // Arm (or disarm) the viewer's pick-point tool. While armed, a left click in
  // the 3D scene raycasts terrain + objects and the viewer replies with exactly
  // one `pointPicked` carrying the snapped WGS84 coordinate; a click that hits
  // nothing (the sky) stays silent. Absolute state — the host owns it and it
  // never self-clears, so ALWAYS send `enabled:false` to disarm. Boolean is not
  // a numeric field, so the integer-only wire rule doesn't apply. Built for
  // exactly this editor: click the 3D map to set a stop's location + elevation.
  | { type: 'pickPoint'; enabled: boolean }
  // Arm (or disarm) free-fly camera mode. While armed the orbit controller is
  // suspended and WASD + Q/E fly the camera with right-drag to look — for
  // navigating INTO underground geometry (mine tunnels / pit) to place stops
  // there. Absolute host-owned state (send `enabled:false` to disarm); pairs
  // with pickPoint (left-click still drops a stop). Boolean, so no wire rule.
  | { type: 'flyCam'; enabled: boolean }
  // 3-mode camera (orientation editor), ported from MineZero: 'freeLook' = WASD
  // drone move + RMB-hold look; 'freeOrbit' = RMB orbits a cursor-raycast pivot
  // + MMB pan + wheel zoom; 'walk' = grounded first-person (pending). Supersedes
  // flyCam (freeLook is the fly camera).
  // `params` carries per-mode tuning, integer-encoded (the wire rule). Added
  // additively to the viewer (no protocol-version bump, like the `ordered`
  // ghostMarkers flag): a viewer built before it ignores the extra field and
  // just switches mode. Sent alongside the mode and re-sent on every slider
  // change (the viewer applies params even when the mode is unchanged).
  | { type: 'setCameraMode'; mode: 'freeLook' | 'freeOrbit' | 'walk'; params?: CameraParamsWire }
  // Show/hide the tag markers (DOM billboards over located twins). Default
  // visible. Additive like setCameraMode: a pre-existing /bevy dist drops it
  // silently, leaving the markers shown. Boolean, so no wire rule.
  | { type: 'setTagMarkersVisible'; visible: boolean }
  // The host's display units (platform level 32): every length the viewer
  // prints (drag distance, path vertex labels, survey readouts) follows it.
  // Additive (older /bevy drops it, staying metric).
  | { type: 'setUnits'; system: 'metric' | 'imperial' }
  // Toggle a twin's relationship lines in the viewer (the web analogue of
  // Unreal's per-twin "Toggle Relationship Lines"): shown=true adds the twin to
  // the viewer's shown set, false removes it. Additive (older /bevy drops it).
  | { type: 'setRelationships'; twinId: string; shown: boolean }
  // Draw ALL relationship edges between anchored twins (the global show-all),
  // independent of the per-twin setRelationships toggle. Additive.
  | { type: 'setRelationshipLinesVisible'; visible: boolean }
  // Show/clear the interactive STOP MARKER — the tall glowing "tube" the author
  // flies into to start/advance a preview. ALL coords present = show at that geo
  // point; any absent = clear. The viewer posts stopMarkerReached/stopMarkerLeft
  // as the camera enters/leaves it. Additive (older /bevy drops it silently).
  | { type: 'showStopMarker'; latE7?: number; lonE7?: number; elevMilli?: number }
  // Show a floating info panel for ONE twin, listing a chosen set of its
  // property + telemetry VALUES. The viewer resolves the live values (the core
  // primitive_property_* tables + telemetry streams) and the twin's screen
  // position and replies with `twinInfo` (and `twinInfoEnd` when cleared) — the
  // host (this editor) draws the card. The editor sends only NAMES. An empty
  // twinId clears; both name arrays empty shows every property and telemetry
  // of the twin. Additive viewer/editor-only verb
  // (no protocol-version bump); a /bevy dist built before it silently drops it,
  // so the panel is a no-op until the viewer bundle is republished.
  | { type: 'showTwinInfo'; twinId: string; propertyNames: string[]; telemetryNames: string[] }
  // Arm (or disarm) the viewer's measure tool (level 33). Absolute host-owned
  // state like pickPoint, and one tool slot with it: arming either disarms the
  // other. While armed the viewer posts `measureChanged` on every change.
  // `polyline` absent = two-point mode; `color` is #rrggbb (absent = viewer
  // default). The viewer's midpoint labels follow `setUnits`.
  // `snap` (level 34; ignored below): absent = true, false places under the
  // cursor and Shift snaps once. `mode` (level 35; ignored below, which stays
  // in points): absent = 'points'; 'size' pins a clicked twin's bounding box.
  | {
      type: 'measure';
      enabled: boolean;
      polyline?: boolean;
      snap?: boolean;
      color?: string;
      mode?: 'points' | 'size';
    };

/** One row of a `twinInfo` panel: a property or telemetry name with its current
 *  value (pre-formatted by the viewer). `present` is false when the value
 *  hasn't resolved yet (the host renders a placeholder). */
export type TwinInfoField = {
  label: string;
  value: string;
  source: 'property' | 'telemetry';
  present: boolean;
};

/** One placed measure point: degrees x 1e7, orthometric elevation in mm. */
export type MeasurePoint = { latE7: number; lonE7: number; elevMm: number };

/** A pinned twin's oriented bounding box (`measureChanged.size`, level 35).
 *  Metres along the asset's own axes, rest pose: `lengthM` the longer
 *  horizontal extent, `widthM` the other, `heightM` vertical; `footprintM2` =
 *  length x width. `source` is `stored` until the mesh streams in. */
export type MeasureSize = {
  twinId: string;
  lengthM: number;
  widthM: number;
  heightM: number;
  footprintM2: number;
  source: 'mesh' | 'stored';
};

/** Viewer → host. */
export type OutboundMessage =
  // `protocolVersion` = the viewer's VIEWER_PROTOCOL_VERSION; absent on a dist
  // that predates it (treat as 0).
  | { type: 'ready'; protocolVersion?: number }
  | { type: 'boot'; phase: string; current?: number; loaded?: number; total?: number }
  | { type: 'selected'; twinId: string }
  // A twin was DOUBLE-clicked in the 3D scene (distinct from single-click
  // `selected`). Used as a deliberate "pick this twin" gesture — the
  // World-selection check adds its expected twins this way.
  | { type: 'twinDoubleClicked'; twinId: string }
  // The camera entered / left the active stop marker's tube (showStopMarker).
  // The host treats "reached" as "arrived at the stop" and shows the Start/Advance
  // prompt; "left" dismisses it.
  | { type: 'stopMarkerReached' }
  | { type: 'stopMarkerLeft' }
  // E7 reply to `getCameraPose`, echoing its requestId — the bridge's only
  // correlated response. lat/lon/elev = the CAMERA position; focus* = the orbit
  // pivot (WGS84 ellipsoidal metres). headingDeg is a compass bearing, pitchDeg
  // negative looking down — exactly what `flyToGeo` accepts, so a captured pose
  // feeds straight back into a fly-to. Outbound floats are fine.
  | {
      type: 'cameraPose';
      requestId: string;
      lat: number;
      lon: number;
      elev: number;
      headingDeg: number;
      pitchDeg: number;
      rangeM: number;
      focusLat: number;
      focusLon: number;
      focusElev: number;
    }
  // Reply to an armed `pickPoint`: the WGS84 coordinate a left click snapped to.
  // Only sent while the tool is armed and only when the click hit geometry (a
  // sky-click sends nothing). `datum` is 'orthometric' for a basemap DEM hit
  // (the common terrain case) and 'ellipsoidal' for a surface/twin-mesh hit —
  // the stop stores `elev` as-is; the ~geoid offset is well within the tall
  // ghost-spike tolerance and the fly-to orbit range.
  | {
      type: 'pointPicked';
      lat: number;
      lon: number;
      elev: number;
      datum: 'ellipsoidal' | 'orthometric';
    }
  // A camera tuning value changed IN the viewer (Free Look +/- or Shift+wheel
  // speed). Mirrors the host's own freeLookSpeedMm encoding (mm/s); the host
  // updates its Camera Controls UI, and echoing the same value back in its
  // next setCameraMode re-send is a no-op. Sent only by a TurboTwin >= today's
  // stamp; older dists simply never send it.
  | { type: 'cameraParamChanged'; mode: string; freeLookSpeedMm: number }
  // ShowTwinInfo panel data for a twin: `fields` are the resolved property +
  // telemetry rows, anchored at (x, y) — the twin's projected screen position
  // in CSS px relative to the viewer canvas. `twinInfoEnd` hides the panel.
  | { type: 'twinInfo'; twinId: string; name: string; fields: TwinInfoField[]; x: number; y: number }
  | { type: 'twinInfoEnd' }
  // The armed measure tool's measurement changed (level 33). Lengths in metres
  // (straight lines, local ENU). While `placing`, the live segment to the
  // cursor is the last `segmentsM` entry and counts in `totalM`; riseM /
  // slopeDeg describe that current segment. `areaM2` only for a closed loop.
  // `armed: false` = the tool is off (host or a second Escape in the viewer):
  // drop the host's button state.
  | {
      type: 'measureChanged';
      state: 'idle' | 'placing' | 'finished';
      points: MeasurePoint[];
      segmentsM: number[];
      totalM: number;
      riseM: number;
      slopeDeg: number;
      areaM2?: number;
      closed: boolean;
      armed: boolean;
      // Size mode (level 35): the pinned box, on a `finished` state with no
      // points and zero segment fields. Absent otherwise.
      size?: MeasureSize;
    }
  | { type: 'error'; message: string };

/**
 * A viewer `error` that reports a dropped module socket ("asset module
 * unavailable (db): disconnected: clean"). The viewer re-opens that connection
 * itself on its next retry and sends no "recovered" message, so a banner for it
 * must clear on its own. A reconnect that fails arrives as a separate error
 * without "disconnected:", which stays latched.
 */
export function isTransientViewerError(message: string): boolean {
  return /module unavailable \(.*\): disconnected: /.test(message);
}
